import { GROUP_BY_FIELDS, GROUP_BY_FIELD_NAMES } from '../components/forms/groupByConfigurations/groupByConstants';
import { METADATA_NAMES } from '../form/formConstants';
import type { RankFieldConfig } from '../generateReport/reportTypes';

export const ENTITIES = {
    workspace: 'workspace',
    submission: 'submission',
    shortlist: 'shortlist'
} as const;

export type Entity = (typeof ENTITIES)[keyof typeof ENTITIES];

const WORKSPACE_CUSTOM_SORT_FIELD_CONFIGS: RankFieldConfig[] = [
    {
        groupId: GROUP_BY_FIELDS.productionStatus,
        name: GROUP_BY_FIELD_NAMES.productionStatusRankOrder,
        groupByLabelToken: 'production.form.field.production.status',
        labelToken: 'form.field.status.rank.order',
        metadataName: METADATA_NAMES.productionStatus,
        sortBy: 'order',
        isEntity: false
    },
    {
        groupId: GROUP_BY_FIELDS.projectStatus,
        name: GROUP_BY_FIELD_NAMES.projectStatusRankOrder,
        groupByLabelToken: 'production.form.field.project.status',
        labelToken: 'form.field.project.status.rank.order',
        metadataName: METADATA_NAMES.productionProjectStatus,
        sortBy: 'order',
        isEntity: false
    },
    {
        groupId: GROUP_BY_FIELDS.productionType,
        name: GROUP_BY_FIELD_NAMES.productionTypeRankOrder,
        groupByLabelToken: 'production.form.field.type',
        sortId: 'productiontype',
        labelToken: 'form.field.production.type.rank.order',
        metadataName: METADATA_NAMES.showType,
        isEntity: false
    },
    {
        groupId: GROUP_BY_FIELDS.productionSubType,
        name: GROUP_BY_FIELD_NAMES.productionSubTypeRankOrder,
        groupByLabelToken: 'production.form.field.subtype',
        sortId: 'productionsubtype',
        labelToken: 'form.field.production.subtype.rank.order',
        metadataName: METADATA_NAMES.productionSubtype,
        isEntity: false
    },
    {
        groupId: GROUP_BY_FIELDS.associatedOrg,
        name: GROUP_BY_FIELD_NAMES.associatedOrgRankOrder,
        groupByLabelToken: 'form.field.associated.org',
        labelToken: 'form.field.associated.org.rank.order',
        metadataName: METADATA_NAMES.associatedOrg,
        isEntity: true
    },
    {
        groupId: GROUP_BY_FIELDS.associatedOrgProductionRole,
        name: GROUP_BY_FIELD_NAMES.associatedOrgProductionRoleRankOrder,
        groupByLabelToken: 'form.field.associated.org.production.role',
        labelToken: 'form.field.associated.org.production.role.rank.order',
        metadataName: METADATA_NAMES.organizationRole,
        isEntity: false
    },
    {
        groupId: GROUP_BY_FIELDS.linkedPerson,
        name: GROUP_BY_FIELD_NAMES.linkedPersonRankOrder,
        groupByLabelToken: 'form.field.linked.person',
        labelToken: 'form.field.linked.person.rank.order',
        metadataName: METADATA_NAMES.linkedPerson,
        isEntity: true
    },
    {
        groupId: GROUP_BY_FIELDS.linkedPersonProductionRole,
        name: GROUP_BY_FIELD_NAMES.linkedPersonProductionRoleRankOrder,
        groupByLabelToken: 'form.field.linked.person.production.role',
        labelToken: 'form.field.linked.person.production.role.rank.order',
        metadataName: METADATA_NAMES.role,
        isEntity: false
    }
];

const SUBMISSION_CUSTOM_SORT_FIELD_CONFIGS: RankFieldConfig[] = [
    {
        groupId: GROUP_BY_FIELDS.projectStatus,
        name: GROUP_BY_FIELD_NAMES.projectStatusRankOrder,
        groupByLabelToken: 'production.form.field.project.status',
        labelToken: 'form.field.project.status.rank.order',
        metadataName: METADATA_NAMES.creativeSubmissionStatus,
        sortBy: 'order',
        isEntity: false
    },
    {
        groupId: GROUP_BY_FIELDS.productionType,
        name: GROUP_BY_FIELD_NAMES.productionTypeRankOrder,
        groupByLabelToken: 'production.form.field.type',
        sortId: 'productiontype',
        labelToken: 'form.field.production.type.rank.order',
        metadataName: METADATA_NAMES.showType,
        isEntity: false
    },
    {
        groupId: GROUP_BY_FIELDS.productionGroups,
        name: GROUP_BY_FIELD_NAMES.productionGroupsRankOrder,
        groupByLabelToken: 'production.group.label',
        sortId: 'group',
        labelToken: 'form.field.production.group.rank.order',
        metadataName: METADATA_NAMES.group,
        isEntity: false
    }
];

export const SHORTLIST_CUSTOM_SORT_FIELD_CONFIGS = [
    {
        groupId: GROUP_BY_FIELDS.itemStatus,
        name: GROUP_BY_FIELD_NAMES.itemStatusRankOrder,
        groupByLabelToken: 'shortlist.display.settings.order.field.itemStatus',
        labelToken: 'form.field.shortlist.itemstatus.rank.order',
        metadataName: METADATA_NAMES.shortlistItemStatus,
        sortBy: 'order',
        isEntity: false
    },
    {
        groupId: GROUP_BY_FIELDS.representativesFullName,
        name: GROUP_BY_FIELD_NAMES.representativesFullNameRankOrder,
        groupByLabelToken: 'shortlist.display.settings.order.field.representativesFullName',
        labelToken: 'form.field.shortlist.representativesFullName.rank.order',
        metadataName: METADATA_NAMES.representativesFullName,
        isEntity: true
    },
    {
        groupId: GROUP_BY_FIELDS.keyRoles,
        name: GROUP_BY_FIELD_NAMES.keyRolesRankOrder,
        groupByLabelToken: 'shortlist.display.settings.order.field.keyRoles',
        labelToken: 'form.field.shortlist.keyRoles.rank.order',
        metadataName: METADATA_NAMES.keyRoles,
        isEntity: false
    }
] satisfies RankFieldConfig[];

export const CUSTOM_SORT_FIELD_CONFIGS_BY_ENTITY: Record<Entity, RankFieldConfig[]> = {
    [ENTITIES.workspace]: WORKSPACE_CUSTOM_SORT_FIELD_CONFIGS,
    [ENTITIES.submission]: SUBMISSION_CUSTOM_SORT_FIELD_CONFIGS,
    [ENTITIES.shortlist]: SHORTLIST_CUSTOM_SORT_FIELD_CONFIGS
};

export const getCustomSortFieldConfigs = (entity: Entity): RankFieldConfig[] => {
    return CUSTOM_SORT_FIELD_CONFIGS_BY_ENTITY[entity] || [];
};

export const getCustomSortFieldConfigMap = (entity: Entity): Record<string, RankFieldConfig> => {
    return Object.fromEntries(getCustomSortFieldConfigs(entity).map((config) => [config.groupId, config]));
};

export const getCustomSortRankOrderFields = (entity: Entity): Record<string, string> => {
    return Object.fromEntries(getCustomSortFieldConfigs(entity).map((config) => [config.groupId, config.name]));
};

export const getCustomSortRankOrderFieldNames = (entity: Entity): string[] => {
    return getCustomSortFieldConfigs(entity).map((config) => config.name);
};
